import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { AumicoCoreProvider, NotificationType } from '../../../../aumico-lib-core-fe/src/lib';
import * as fromActions from '../actions/annotations.actions';
import { CreateAnnotationDto } from '../dtos/create-annotation-dto.interface';
import { Annotation } from '../interfaces/annotation.interface';
import { AnnotationsService } from '../services/annotations.service';

@Injectable()
export class AnnotationsEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly annotationsService: AnnotationsService,
        private coreProvider: AumicoCoreProvider,
    ) {}

    updateAnnotation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.updateAnnotation.do),
            mergeMap((action) =>
                this.annotationsService.updateAnnotation(action.annotationId, action.payload).pipe(
                    map((response) => {
                        this.coreProvider.dispatchAddNotification(
                            'aumico_cpmm_annotation_update_success_title',
                            'aumico_cpmm_annotation_update_success_description',
                            NotificationType.SUCCESS,
                        );
                        return fromActions.updateAnnotation.success({ payload: response });
                    }),
                    catchError((error) => {
                        this.coreProvider.dispatchAddNotification(
                            'aumico_cpmm_annotation_update_failure_title',
                            'aumico_cpmm_annotation_update_failure_description',
                            NotificationType.ERROR,
                        );
                        return of(fromActions.updateAnnotation.failure({ payload: error }));
                    }),
                ),
            ),
        ),
    );

    createAnnotation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.createAnnotation.do.type),
            mergeMap(({ payload }: fromActions.ActionWithPayload<{ taskId: string; payload: CreateAnnotationDto }>) => {
                return this.annotationsService.createAnnotation(payload.taskId, payload.payload).pipe(
                    map((response: Annotation) => {
                        this.coreProvider.dispatchAddNotification(
                            'aumico_cpmm_annotation_create_success_title',
                            'aumico_cpmm_annotation_create_success_description',
                            NotificationType.SUCCESS,
                        );
                        return fromActions.createAnnotation.success({
                            payload: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        this.coreProvider.dispatchAddNotification(
                            'aumico_cpmm_annotation_create_failure_title',
                            'aumico_cpmm_annotation_create_failure_description',
                            NotificationType.ERROR,
                        );
                        return of(fromActions.createAnnotation.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    getTaskAnnotations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getTaskAnnotations.do.type),
            mergeMap(({ payload }: fromActions.ActionWithPayload<{ taskId: string; page: number; limit: number }>) => {
                return this.annotationsService.getTaskAnnotations(payload.taskId, payload.page, payload.limit).pipe(
                    map((response) => {
                        return fromActions.getTaskAnnotations.success({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.getTaskAnnotations.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    deleteAnnotation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.deleteAnnotation.do),
            mergeMap((action) =>
                this.annotationsService.deleteAnnotation(action.payload).pipe(
                    map(() => {
                        this.coreProvider.dispatchAddNotification(
                            'aumico_cpmm_annotation_delete_success_title',
                            'aumico_cpmm_annotation_delete_success_description',
                            NotificationType.SUCCESS,
                        );
                        return fromActions.deleteAnnotation.success({ payload: action.payload });
                    }),
                    catchError((error) => {
                        this.coreProvider.dispatchAddNotification(
                            'aumico_cpmm_annotation_delete_failure_title',
                            'aumico_cpmm_annotation_delete_failure_description',
                            NotificationType.ERROR,
                        );
                        return of(fromActions.deleteAnnotation.failure({ payload: error }));
                    }),
                ),
            ),
        ),
    );
}
